import { createContext, useContext, useEffect, useState } from 'react'
import { AlertColor, AlertPropsColorOverrides } from '@mui/material'
import { CheckCircle } from '@mui/icons-material'

import { useUser } from 'app/User'
import { useGetTrayItemsPaginated } from 'lib/apollo/hooks'
import { extractTrayJSONData } from 'lib/utils/extractTrayJSONData'

import { OverridableStringUnion } from '@mui/types'
import { TrayType } from 'components/organisms/AddTraySelectType/AddTraySelectType.types'
import { SPDContextProps } from 'views/SPDLayout/SPD/SPD.types'
import { ItemResponse, Option } from 'common/types'
import { FilterValue } from 'components/organisms/TrayManagementFilters/TrayManagementFilters.types'
import { TrayItemProductDetails } from 'lib/services/api/product-service/receiveItems/types'
import { TrayStatus } from 'components/organisms/EditTrayModal/EditTrayModal.types'
import { useLocation } from 'react-router-dom'
import {
  GetTrayItemsPaginatedInput,
  TrayManagementContextProps,
} from './TrayManagement.types'
import dayjs from 'lib/dayjs'
import { DateRangeFilter } from 'components/organisms/FilterDrawer/FilterDrawer.types'

const TrayManagementContext = createContext<
  TrayManagementContextProps | undefined
>(undefined)

export const TrayManagementProvider = ({
  children,
}: {
  children: JSX.Element
}) => {
  const { isRep, user } = useUser()
  const { pathname } = useLocation()

  const [trays, setTrays] = useState<ItemResponse[]>([])
  const [selectedTrayCategory, setSelectedTrayCategory] = useState<
    TrayType['category'] | null
  >(null)
  const [selectedTray, setSelectedTray] = useState<
    SPDContextProps['trayData'] | null
  >(null)
  const [isListOfSurgeriesOpen, setIsListOfSurgeriesOpen] =
    useState<boolean>(false)
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false)
  const [snackbarState, setSnackbarState] = useState<
    OverridableStringUnion<AlertColor, AlertPropsColorOverrides> | undefined
  >('success')
  const [snackbarMessage, setSnackbarMessage] = useState<string>('')
  const [snackbarIcon, setSnackbarIcon] = useState<JSX.Element>(
    <CheckCircle color="success" />
  )
  const [filterValue, setFilterValue] = useState<FilterValue>('')
  const [trayIdFilterValue, setTrayIdFilterValue] = useState<string>('')
  const [companyFilterValue, setCompanyFilterValue] = useState<
    Option | undefined
  >(undefined)
  const [trayStatusFilterValue, setTrayStatusFilterValue] =
    useState<TrayStatus>(undefined)
  const [isAddTrayModalOpen, setIsAddTrayModalOpen] = useState<boolean>(false)
  const [isFiltersNavbarOpen, setIsFiltersNavbarOpen] = useState<boolean>(true)
  const [isEditTrayStatusModalOpen, setIsEditTrayStatusModalOpen] =
    useState<boolean>(false)
  const [selectedFilters, setSelectedFilters] = useState<Set<TrayStatus>>(
    new Set()
  )
  const [selectedCategory, setSelectedCategory] = useState<
    TrayType['category'] | undefined
  >(undefined)
  const [lastViewedTrayId, setLastViewedTrayId] = useState<string | null>(null)
  const [surgeonFilterValue, setSurgeonFilterValue] = useState<string>('')
  const [patientFilterValue, setPatientFilterValue] = useState<string>('')
  const [roomFilterValue, setRoomFilterValue] = useState<string>('')
  const [dateFilterValue, setDateFilterValue] = useState<DateRangeFilter>({
    startDate: null,
    endDate: null,
  })
  const [creationDateFilterValue, setCreationDateFilterValue] =
    useState<DateRangeFilter>({
      startDate: null,
      endDate: null,
    })

  // Build filters for GraphQL query
  const buildFilters = (): Omit<
    GetTrayItemsPaginatedInput,
    'assignmentStatus' | 'limit' | 'offset'
  > => {
    const statuses =
      selectedFilters.size > 0
        ? Array.from(selectedFilters).filter(
            (status): status is Exclude<TrayStatus, undefined> =>
              status !== undefined
          )
        : undefined

    const dateRange =
      dateFilterValue.startDate || dateFilterValue.endDate
        ? {
            ...(dateFilterValue.startDate && {
              startDate: dateFilterValue.startDate,
            }),
            ...(dateFilterValue.endDate && {
              endDate:
                dateFilterValue.startDate === dateFilterValue.endDate
                  ? dayjs(dateFilterValue.endDate, 'MM/DD/YYYY')
                      .endOf('day')
                      .format('MM/DD/YYYY HH:mm:ss')
                  : dateFilterValue.endDate,
            }),
          }
        : undefined

    const creationDateRange =
      creationDateFilterValue.startDate || creationDateFilterValue.endDate
        ? {
            ...(creationDateFilterValue.startDate && {
              startDate: creationDateFilterValue.startDate,
            }),
            ...(creationDateFilterValue.endDate && {
              endDate:
                creationDateFilterValue.startDate ===
                creationDateFilterValue.endDate
                  ? dayjs(creationDateFilterValue.endDate, 'MM/DD/YYYY')
                      .endOf('day')
                      .format('MM/DD/YYYY HH:mm:ss')
                  : creationDateFilterValue.endDate,
            }),
          }
        : undefined

    return {
      ...(statuses && { statuses }),
      ...(selectedCategory && { category: selectedCategory }),
      ...(trayIdFilterValue && { trayIdFilter: trayIdFilterValue }),
      ...(companyFilterValue && { companyFilter: companyFilterValue.name }),
      ...(surgeonFilterValue && { surgeonFilter: surgeonFilterValue }),
      ...(patientFilterValue && { patientFilter: patientFilterValue }),
      ...(roomFilterValue && { roomFilter: roomFilterValue }),
      ...(isRep && user?.email && { repEmail: user.email }),
      ...(dateRange && { dateRange }),
      ...(creationDateRange && { creationDateRange }),
    }
  }

  const paginationFilters = buildFilters()

  const {
    assignedTrays,
    unassignedTrays,
    assignedPagination,
    unassignedPagination,
    error,
    loadMoreAssigned,
    loadMoreUnassigned,
    refetch,
    isRefetching,
  } = useGetTrayItemsPaginated(paginationFilters)

  // Combine trays for backward compatibility with existing logic
  useEffect(() => {
    const combinedTrays = [...assignedTrays, ...unassignedTrays]
    setTrays(combinedTrays)
  }, [assignedTrays, unassignedTrays])

  // Refetch when filters change
  useEffect(() => {
    refetch()
  }, [
    selectedFilters,
    selectedCategory,
    trayIdFilterValue,
    companyFilterValue?.name,
    surgeonFilterValue,
    patientFilterValue,
    roomFilterValue,
    dateFilterValue,
    creationDateFilterValue,
    refetch,
  ])

  // Refetch when pathname changes
  useEffect(() => {
    refetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  const handleSnackbarClose = (
    event: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === 'clickaway') {
      return
    }

    setSnackbarOpen(false)
  }

  // Legacy filter functions (keeping for compatibility)
  const applyBarcodeFilter = (
    trayItem: ItemResponse,
    trayIdFilterValue?: string
  ) =>
    trayIdFilterValue
      ? trayItem.barcode
          ?.toLowerCase()
          .includes(trayIdFilterValue.toLowerCase())
      : true

  const applyCompanyFilter = (
    trayItem: ItemResponse,
    companyFilterValue?: { name: string }
  ) =>
    companyFilterValue
      ? trayItem.companyName
          ?.toLowerCase()
          .includes(companyFilterValue.name.toLowerCase())
      : true

  const applyRepEmailFilter = (
    trayDetails: TrayItemProductDetails,
    userEmail?: string,
    isRep?: boolean
  ) => {
    if (isRep && userEmail) {
      return trayDetails.email === userEmail
    }
    return true
  }

  const applySurgeonFilter = (
    trayDetails: TrayItemProductDetails,
    surgeonFilterValue?: string
  ) => {
    if (!surgeonFilterValue) return true

    const surgery = trayDetails.surgeryDetails
    let surgeonName = trayDetails.selectedSurgeon

    if (surgery?.visit?.attendingProvider) {
      const lastName = surgery.visit.attendingProvider.lastName || ''
      const firstNameInitial =
        surgery.visit.attendingProvider.firstName?.charAt(0) || ''
      surgeonName = `${lastName}, ${firstNameInitial}`
    }

    return surgeonName?.toLowerCase().includes(surgeonFilterValue.toLowerCase())
  }

  const applyPatientFilter = (
    trayDetails: TrayItemProductDetails,
    patientFilterValue?: string
  ) => {
    if (!patientFilterValue) return true

    const patient = trayDetails.surgeryDetails?.patient
    if (!patient) return false

    const patientFullName = `${patient.lastName}, ${patient.firstName}`
    return patientFullName
      .toLowerCase()
      .includes(patientFilterValue.toLowerCase())
  }

  const applyRoomFilter = (
    trayDetails: TrayItemProductDetails,
    roomFilterValue?: string
  ) => {
    if (!roomFilterValue) return true

    const room = trayDetails.surgeryDetails?.visit?.location?.room
    return room?.toLowerCase().includes(roomFilterValue.toLowerCase())
  }

  const applyDateFilter = (
    trayDetails: TrayItemProductDetails,
    dateFilterValue?: DateRangeFilter
  ) => {
    if (!dateFilterValue?.startDate && !dateFilterValue?.endDate) return true

    const surgery = trayDetails.surgeryDetails
    if (!surgery) return false

    const earliestDateTime = surgery.procedures
      ?.map((proc) => proc.dateTime)
      .filter(Boolean)
      .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())[0]

    if (!earliestDateTime) return false

    const surgeryDate = dayjs(earliestDateTime)

    // Check if surgery date is within the range
    const startDate = dateFilterValue.startDate
      ? dayjs(dateFilterValue.startDate, 'MM/DD/YYYY')
      : null
    const endDate = dateFilterValue.endDate
      ? dayjs(dateFilterValue.endDate, 'MM/DD/YYYY')
      : null

    if (startDate && endDate) {
      return (
        (surgeryDate.isAfter(startDate, 'day') ||
          surgeryDate.isSame(startDate, 'day')) &&
        (surgeryDate.isBefore(endDate, 'day') ||
          surgeryDate.isSame(endDate, 'day'))
      )
    } else if (startDate) {
      return (
        surgeryDate.isAfter(startDate, 'day') ||
        surgeryDate.isSame(startDate, 'day')
      )
    } else if (endDate) {
      return (
        surgeryDate.isBefore(endDate, 'day') ||
        surgeryDate.isSame(endDate, 'day')
      )
    }

    return true
  }

  const applyCreationDateFilter = (
    trayItem: ItemResponse,
    creationDateFilterValue?: DateRangeFilter
  ) => {
    if (
      !creationDateFilterValue?.startDate &&
      !creationDateFilterValue?.endDate
    )
      return true

    if (!trayItem.createdAt) return false

    const trayCreationDate = dayjs(trayItem.createdAt)

    // Check if creation date is within the range
    const startDate = creationDateFilterValue.startDate
      ? dayjs(creationDateFilterValue.startDate, 'MM/DD/YYYY')
      : null
    const endDate = creationDateFilterValue.endDate
      ? dayjs(creationDateFilterValue.endDate, 'MM/DD/YYYY')
      : null

    if (startDate && endDate) {
      return (
        (trayCreationDate.isAfter(startDate, 'day') ||
          trayCreationDate.isSame(startDate, 'day')) &&
        (trayCreationDate.isBefore(endDate, 'day') ||
          trayCreationDate.isSame(endDate, 'day'))
      )
    } else if (startDate) {
      return (
        trayCreationDate.isAfter(startDate, 'day') ||
        trayCreationDate.isSame(startDate, 'day')
      )
    } else if (endDate) {
      return (
        trayCreationDate.isBefore(endDate, 'day') ||
        trayCreationDate.isSame(endDate, 'day')
      )
    }

    return true
  }

  const filterTrayItems = (
    trayItems: ItemResponse[],
    selectedFilters: Set<TrayStatus>,
    selectedCategory: TrayType['category'] | undefined,
    trayIdFilterValue?: string,
    companyFilterValue?: { name: string },
    isRep?: boolean,
    surgeonFilterValue?: string,
    patientFilterValue?: string,
    roomFilterValue?: string,
    dateFilterValue?: DateRangeFilter,
    creationDateFilterValue?: DateRangeFilter
  ): ItemResponse[] => {
    return trayItems.filter((trayItem) => {
      const trayDetails = extractTrayJSONData(
        trayItem
      ) as TrayItemProductDetails

      return (
        applyRepEmailFilter(trayDetails, user?.email, isRep) &&
        applyCategoryFilter(trayDetails, selectedCategory) &&
        applySelectedFilters(trayDetails, selectedFilters) &&
        applyBarcodeFilter(trayItem, trayIdFilterValue) &&
        applyCompanyFilter(trayItem, companyFilterValue) &&
        applySurgeonFilter(trayDetails, surgeonFilterValue) &&
        applyPatientFilter(trayDetails, patientFilterValue) &&
        applyRoomFilter(trayDetails, roomFilterValue) &&
        applyDateFilter(trayDetails, dateFilterValue) &&
        applyCreationDateFilter(trayItem, creationDateFilterValue)
      )
    })
  }

  const filterTrays = (trays: ItemResponse[]) => {
    return filterTrayItems(
      trays,
      selectedFilters,
      selectedCategory,
      trayIdFilterValue,
      companyFilterValue,
      isRep,
      surgeonFilterValue,
      patientFilterValue,
      roomFilterValue,
      dateFilterValue,
      creationDateFilterValue
    )
  }

  const applyCategoryFilter = (
    trayDetails: TrayItemProductDetails,
    selectedCategory: TrayType['category'] | undefined
  ) => {
    if (!selectedCategory) return true
    return trayDetails.trayCategory === selectedCategory
  }

  const applySelectedFilters = (
    trayDetails: TrayItemProductDetails,
    selectedFilters: Set<TrayStatus>
  ) => {
    if (selectedFilters.size === 0) return true
    return selectedFilters.has(trayDetails.status)
  }

  const handleFiltersNavbarMenuClick = () => {
    setIsFiltersNavbarOpen((prev) => !prev)
  }

  return (
    <TrayManagementContext.Provider
      value={{
        trays,
        setTrays,
        loadingTrays:
          assignedPagination.loading || unassignedPagination.loading,
        error,
        selectedTrayCategory,
        setSelectedTrayCategory,
        selectedTray,
        setSelectedTray,
        isListOfSurgeriesOpen,
        setIsListOfSurgeriesOpen,
        snackbarIcon,
        setSnackbarIcon,
        snackbarState,
        setSnackbarState,
        snackbarOpen,
        setSnackbarOpen,
        snackbarMessage,
        setSnackbarMessage,
        handleSnackbarClose,
        filterValue,
        setFilterValue,
        trayIdFilterValue,
        setTrayIdFilterValue,
        companyFilterValue,
        setCompanyFilterValue,
        trayStatusFilterValue,
        setTrayStatusFilterValue,
        isAddTrayModalOpen,
        setIsAddTrayModalOpen,
        isFiltersNavbarOpen,
        setIsFiltersNavbarOpen,
        handleFiltersNavbarMenuClick,
        refetch,
        isEditTrayStatusModalOpen,
        setIsEditTrayStatusModalOpen,
        selectedFilters,
        setSelectedFilters,
        selectedCategory,
        setSelectedCategory,
        filterTrays,
        lastViewedTrayId,
        setLastViewedTrayId,
        surgeonFilterValue,
        setSurgeonFilterValue,
        patientFilterValue,
        setPatientFilterValue,
        roomFilterValue,
        setRoomFilterValue,
        dateFilterValue,
        setDateFilterValue,
        creationDateFilterValue,
        setCreationDateFilterValue,
        // New pagination properties
        totalCount:
          assignedPagination.totalCount + unassignedPagination.totalCount,
        hasNextPage:
          assignedPagination.hasNextPage || unassignedPagination.hasNextPage,
        loadMore: () => {
          // For backward compatibility, load more from both sections
          if (assignedPagination.hasNextPage) loadMoreAssigned()
          if (unassignedPagination.hasNextPage) loadMoreUnassigned()
        },
        isRefetching,
        // Separate pagination controls
        assignedTrays,
        unassignedTrays,
        assignedPagination,
        unassignedPagination,
        loadMoreAssigned,
        loadMoreUnassigned,
      }}
    >
      {children}
    </TrayManagementContext.Provider>
  )
}

export const useTrayManagementContext = () => {
  const context = useContext(TrayManagementContext)

  if (context === undefined) {
    throw new Error(
      'useTrayManagementContext must be used with TrayManagementProvider'
    )
  }

  return context
}
